import {
  DASHBOARD_WIDGET_DEFINITIONS,
  ENTITLEMENT_PRESETS,
  MODULE_DEFINITIONS,
  MODULE_LEVELS,
  intersectModuleAccess,
  moduleLevelAtLeast,
  normalizeModuleAccess,
  type DashboardWidget,
  type ModuleAccess,
  type ModuleLevel,
  type TopLevelModule,
} from '../../../convex/modules';

type AccessProfileEditorProps = {
  value: ModuleAccess;
  onChange: (access: ModuleAccess) => void;
  title?: string;
  description?: string;
  maximumAccess?: ModuleAccess;
  showPresets?: boolean;
};

function isAllowedByMaximum(maximum: ModuleLevel, requested: ModuleLevel) {
  if (requested === 'off') return true;
  return moduleLevelAtLeast(maximum, requested);
}

export function AccessProfileEditor({
  value,
  onChange,
  title = 'Access profile',
  description,
  maximumAccess,
  showPresets = false,
}: AccessProfileEditorProps) {
  const applyMaximum = (next: ModuleAccess) =>
    maximumAccess ? intersectModuleAccess(maximumAccess, next) : next;

  const setModuleLevel = (moduleId: TopLevelModule, level: ModuleLevel) => {
    onChange(
      applyMaximum({
        ...value,
        modules: {
          ...value.modules,
          [moduleId]: level,
        },
      })
    );
  };

  const toggleWidget = (widgetId: DashboardWidget) => {
    onChange(
      applyMaximum({
        ...value,
        dashboardWidgets: {
          ...value.dashboardWidgets,
          [widgetId]: !value.dashboardWidgets[widgetId],
        },
      })
    );
  };

  const applyPreset = (presetKey: keyof typeof ENTITLEMENT_PRESETS) => {
    const preset = ENTITLEMENT_PRESETS[presetKey];
    onChange(
      applyMaximum(
        normalizeModuleAccess({
          modules: preset.modules,
          dashboardWidgets: value.dashboardWidgets,
        })
      )
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h4>
          {description && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{description}</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {maximumAccess && (
            <button
              type="button"
              onClick={() => onChange(normalizeModuleAccess(maximumAccess))}
              className="px-2 py-1 text-xs rounded-md border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Use all customer access
            </button>
          )}
          {showPresets &&
            (
              Object.keys(ENTITLEMENT_PRESETS) as Array<keyof typeof ENTITLEMENT_PRESETS>
            ).map((presetKey) => (
              <button
                key={presetKey}
                type="button"
                onClick={() => applyPreset(presetKey)}
                className="px-2 py-1 text-xs rounded-md border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                {ENTITLEMENT_PRESETS[presetKey].label}
              </button>
            ))}
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
            Top-level modules
          </h5>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(15rem,1fr))] gap-2">
            {MODULE_DEFINITIONS.map((module) => (
              <div
                key={module.id}
                className="flex min-w-0 items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2.5 dark:border-gray-700"
              >
                <span className="min-w-0 text-sm leading-5 text-gray-700 dark:text-gray-300">
                  {module.label}
                </span>
                <select
                  value={value.modules[module.id]}
                  onChange={(event) =>
                    setModuleLevel(module.id, event.target.value as ModuleLevel)
                  }
                  className="h-9 w-28 shrink-0 rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  {MODULE_LEVELS.map((level) => (
                    <option
                      key={level}
                      value={level}
                      disabled={
                        maximumAccess &&
                        !isAllowedByMaximum(maximumAccess.modules[module.id], level)
                      }
                    >
                      {level === 'off' ? 'Off' : level === 'view' ? 'View' : 'Manage'}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
            Dashboard widgets
          </h5>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(12rem,1fr))] gap-2">
            {DASHBOARD_WIDGET_DEFINITIONS.map((widget) => {
              const available = maximumAccess?.dashboardWidgets[widget.id] ?? true;
              return (
                <label
                  key={widget.id}
                  className={`flex min-h-12 min-w-0 items-center gap-3 rounded-lg border px-3 py-2.5 ${
                    available
                      ? 'border-gray-200 dark:border-gray-700'
                      : 'border-gray-100 bg-gray-50 opacity-60 dark:border-gray-800 dark:bg-gray-900/40'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={value.dashboardWidgets[widget.id]}
                    disabled={!available}
                    onChange={() => toggleWidget(widget.id)}
                  />
                  <span className="min-w-0 text-sm leading-5 text-gray-700 dark:text-gray-300">
                    {widget.label}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
